import React from 'react';
import * as Card from '../../v2BaseComponents/Card';
import { APITypes } from '../../../primary-record-types';
import LargeDateDisplay from '../LargeDateDisplay';
import { getIntl } from '../../../utils/intl';
import getDateString from '../../../utils/getDateString';
import { centsToDollarsConversion } from '../../../utils/centsToDollarsConversion';
import { useCollaboratorInvoices } from '../../../hooks/api/useCollaboratorInvoices';
import { useCollaborators } from '../../../hooks/api/useCollaborators';
import { isOrganizationCollaborator } from '../../../utils/collaboratorUtils';

const intl = getIntl('invoice-card', {
    due: '{base} - Due {date}',
    open: 'Open',
    paid: 'Paid',
    void: 'Voided',
    refunded: 'Refunded',
});

export type InvoiceCardOwnProps = {
    invoice: APITypes.organizationService.OrganizationInvoiceDetails;
    onPress?: () => void;
};

export type InvoiceCardProps = InvoiceCardOwnProps;

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, onPress }) => {
    let header = `\$${centsToDollarsConversion.convertCentsToDollars(invoice.amount)}`;
    header =
        invoice.status === 'open'
            ? intl('due', { date: getDateString(invoice.dueDateTime, 'date'), base: header })
            : header;

    return (
        <Card.Card onPress={onPress} testID="invoice-card">
            <Card.CardLeftContent leftContent={<LargeDateDisplay date={invoice.createdDateTime} />}>
                <Card.CardContent>
                    <Card.CardHeader>{header}</Card.CardHeader>
                    <Card.CardChip title={intl(invoice.status)} />
                    <Card.CardCaption>{invoice.description}</Card.CardCaption>
                </Card.CardContent>
            </Card.CardLeftContent>
        </Card.Card>
    );
};

export default React.memo(InvoiceCard);

export type UnloadedInvoiceCardProps = {
    invoiceId: string;
    onPress?: () => void;
    medicalProfileId: string;
    collaboratorId: string;
};

export const UnloadedInvoiceCard: React.FC<UnloadedInvoiceCardProps> = ({
    invoiceId,
    onPress,
    medicalProfileId,
    collaboratorId,
}) => {
    const { isLoadingInvoice, invoice } = useCollaboratorInvoices({
        invoiceId,
        medicalProfileId,
        collaboratorId,
        isOrganizationCollaborator: true,
    });

    if (isLoadingInvoice && !invoice) {
        return <Card.CardSkeleton />;
    } else if (!invoice) {
        return null;
    } else {
        return <InvoiceCard invoice={invoice.invoice} onPress={onPress} />;
    }
};

export type UnloadedOrgInvoiceIdInvoiceCardOwnProps = {
    invoiceId: string;
    organizationId: string;
    onPress?: (collaboratorId: string) => void;
    medicalProfileId: string;
};

export type UnloadedOrgInvoiceIdInvoiceCardProps = UnloadedOrgInvoiceIdInvoiceCardOwnProps;

export const UnloadedOrgInvoiceIdInvoiceCard: React.FC<UnloadedOrgInvoiceIdInvoiceCardProps> = ({
    invoiceId,
    organizationId,
    onPress,
    medicalProfileId,
}) => {
    const { isLoadingCollaborators, collaborators } = useCollaborators({
        medicalProfileId,
    });

    if (isLoadingCollaborators) {
        return <Card.CardSkeleton />;
    }

    const collaborator = collaborators.find(
        (_) => isOrganizationCollaborator(_) && _.organization.organizationId === organizationId,
    );

    if (!collaborator) {
        return null;
    }

    return (
        <UnloadedInvoiceCard
            invoiceId={invoiceId}
            onPress={() => onPress?.(collaborator.medicalProfileCollaboratorId)}
            medicalProfileId={medicalProfileId}
            collaboratorId={collaborator.medicalProfileCollaboratorId}
        />
    );
};
